/**
 * Manda la foto del comprobante a WhatsApp al apretar un botón.
 *
 * Realidad de WhatsApp: NO existe forma de adjuntar una imagen por un link
 * (wa.me / web.whatsapp.com/send solo llevan texto). Entonces:
 *  - Celular: usa el "Compartir" nativo (navigator.share) → la foto viaja ADJUNTA
 *    de verdad y el usuario elige el chat.
 *  - WhatsApp Web (compu): copia la foto al PORTAPAPELES (como PNG) y abre el chat;
 *    el usuario solo pega con Ctrl+V (Cmd+V en Mac) y Enter.
 *  - Fallback (Firefox / sin https / sin portapapeles): descarga la foto + abre el
 *    chat, y el usuario la arrastra.
 *
 * IMPORTANTE: llamar DIRECTO desde el onPress, sin ningún await antes, para no
 * matar el "gesto de usuario" (transient activation) que exige el portapapeles.
 */

export type EstadoEnvio =
  | { tipo: 'compartido' } // celular: se abrió el compartir
  | { tipo: 'cancelado' } // celular: el usuario cerró el compartir
  | { tipo: 'copiado' } // compu: foto copiada + chat abierto → pegar
  | { tipo: 'popupBloqueado'; abrir: () => void } // el navegador bloqueó la pestaña
  | { tipo: 'descargado'; abrir: () => void }; // fallback: se bajó la foto

export interface EnvioParams {
  imageUrl?: string | null;
  numero: string;
  caption?: string;
  onEstado?: (e: EstadoEnvio) => void;
}

function normalizarNumero(input: string): string {
  const d = input.replace(/\D/g, '');
  return d.startsWith('591') ? d : `591${d}`;
}

export function esMac(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad|iPod/i.test((navigator as any).platform || navigator.userAgent);
}

function esMovil(): boolean {
  if (typeof navigator === 'undefined') return false;
  const uaData = (navigator as any).userAgentData;
  if (uaData && typeof uaData.mobile === 'boolean') return uaData.mobile;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

function urlChat(numero: string, caption?: string): string {
  const t = caption ? `&text=${encodeURIComponent(caption)}` : '';
  // web.whatsapp.com/send es más directo que wa.me (que mete una interstitial).
  return `https://web.whatsapp.com/send?phone=${normalizarNumero(numero)}${t}`;
}

// window.open sin 'noopener' para poder detectar el bloqueo (w === null).
function abrirChat(numero: string, caption?: string): boolean {
  const w = window.open(urlChat(numero, caption), '_blank');
  return w !== null;
}

// Cloudinary: pedir la imagen ya en PNG (el portapapeles solo acepta image/png).
function urlPng(url: string): string {
  return url.replace(/\/(image|video|raw)\/upload\//, '/$1/upload/f_png/');
}
// Cloudinary: forzar descarga como archivo.
function urlDescarga(url: string): string {
  return url.replace(/\/(image|video|raw)\/upload\//, '/$1/upload/fl_attachment/');
}

function descargar(url: string) {
  const a = document.createElement('a');
  a.href = urlDescarga(url);
  a.download = 'comprobante';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// Baja la imagen ya como PNG. Si por algo no vino PNG, la convierte con canvas.
// (Cloudinary de entrega manda Access-Control-Allow-Origin: * → fetch CORS ok.)
async function aPngBlob(url: string): Promise<Blob> {
  const resp = await fetch(urlPng(url), { mode: 'cors', credentials: 'omit' });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const blob = await resp.blob();
  if (blob.type === 'image/png') return blob;
  const bmp = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bmp.width;
  canvas.height = bmp.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('sin contexto 2d');
  ctx.drawImage(bmp, 0, 0);
  bmp.close?.();
  const png = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/png'));
  if (!png) throw new Error('toBlob null');
  return png;
}

export function enviarComprobanteWhatsApp({ imageUrl, numero, caption, onEstado }: EnvioParams): void {
  const notify = (e: EstadoEnvio) => onEstado?.(e);

  // Sin foto: solo abrir el chat con el texto.
  if (!imageUrl) {
    const abierto = abrirChat(numero, caption);
    if (!abierto) notify({ tipo: 'popupBloqueado', abrir: () => abrirChat(numero, caption) });
    return;
  }

  // ---- CELULAR: Web Share con la foto real ----
  if (esMovil() && typeof navigator !== 'undefined' && typeof navigator.canShare === 'function') {
    (async () => {
      try {
        const resp = await fetch(imageUrl, { mode: 'cors', credentials: 'omit' });
        const blob = await resp.blob();
        const file = new File([blob], 'comprobante.jpg', { type: blob.type || 'image/jpeg' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text: caption });
          notify({ tipo: 'compartido' });
          return;
        }
      } catch (err: any) {
        if (err?.name === 'AbortError') {
          notify({ tipo: 'cancelado' });
          return;
        }
      }
      descargar(imageUrl);
      notify({ tipo: 'descargado', abrir: () => abrirChat(numero, caption) });
    })();
    return;
  }

  // ---- COMPU (WhatsApp Web): portapapeles + abrir chat ----
  const puedeClipboard =
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    !!navigator.clipboard &&
    typeof navigator.clipboard.write === 'function' &&
    typeof ClipboardItem !== 'undefined';

  if (!puedeClipboard) {
    descargar(imageUrl);
    notify({ tipo: 'descargado', abrir: () => abrirChat(numero, caption) });
    return;
  }

  // El Blob va como PROMESA dentro del ClipboardItem: el fetch + conversión ocurren
  // dentro sin matar el gesto de usuario. write se llama sincrónico (sin await antes).
  const pngPromise = aPngBlob(imageUrl);
  navigator.clipboard
    .write([new ClipboardItem({ 'image/png': pngPromise as any })])
    .then(() => {
      // Copiar PRIMERO (ya hecho), abrir el chat DESPUÉS (abrir roba el foco).
      const abierto = abrirChat(numero, caption);
      notify(abierto ? { tipo: 'copiado' } : { tipo: 'popupBloqueado', abrir: () => abrirChat(numero, caption) });
    })
    .catch(() => {
      descargar(imageUrl);
      notify({ tipo: 'descargado', abrir: () => abrirChat(numero, caption) });
    });
}
